import React, { useRef, useState } from "react";
import { FaPaperclip, FaTimes } from "react-icons/fa";
import {
  useGetTaskCommentsQuery,
  useCreateTaskCommentMutation,
  useUpdateTaskCommentMutation,
  useDeleteTaskCommentMutation,
  useDeleteTaskAttachmentMutation,
} from "../../store";
import useAuth from "../../Hooks/useAuth";
import { useAlert } from "../../context/AlertContext";
import RichTextEditor from "./RichTextEditor";
import RichTextView from "./RichTextView";
import AttachmentList from "./AttachmentList";
import UserChip from "./atoms/UserChip";
import TaskConfirmModal, { OVER_DRAWER_Z } from "./TaskConfirmModal";
import useAttachmentUpload, { useTaskImageResolver } from "./useAttachmentUpload";
import { relativeTime, formatDateTime } from "./taskConstants";
import { useDirtyDraft } from "./nestedOverlay";

// Gövdede satır içi görsel olarak geçen ek id'leri (data-attachment-id)
export const inlineAttachmentIds = (html) =>
  Array.from(String(html || "").matchAll(/data-attachment-id="([^"]+)"/g), (m) => m[1]);

// Yorumlar sekmesi — eskiden yeniye, 50'lik sayfalar ("Daha fazla").
// Mutasyonlar gövdede `task` taşır (hedefli cache tazeleme).
export const COMMENT_PAGE = 50;
const MAX_BODY = 20 * 1024;
const errText = (e, fallback) => e?.data?.message || e?.message || fallback;

const btn = "px-3 py-1.5 rounded-lg text-sm font-Medium disabled:opacity-50";
const btnPrimary = `${btn} bg-main text-white hover:opacity-90`;
const btnGhost = `${btn} text-gray-600 hover:bg-gray-100`;

function CommentItem({ comment, taskId, resolveImage, onUploadImage, onDelete }) {
  const { auth } = useAuth();
  const { showError } = useAlert();
  const [updateComment, { isLoading: saving }] = useUpdateTaskCommentMutation();
  const [deleteAttachment] = useDeleteTaskAttachmentMutation();
  const [draft, setDraft] = useState(null);
  useDirtyDraft(draft !== null && draft !== (comment.body || ""));

  const save = async () => {
    if (!draft) return;
    try {
      await updateComment({ token: auth?.accessToken, id: comment._id, body: draft, task: taskId }).unwrap();
      setDraft(null);
    } catch (e) {
      showError(errText(e, "Yorum kaydedilemedi."));
    }
  };

  // Gövdede satır içi görünen görseller ek listesinde tekrar gösterilmez
  const inlineIds = new Set(inlineAttachmentIds(comment.body));
  const fileAttachments = (comment.attachments || []).filter((a) => !inlineIds.has(a._id));

  const removeAttachment = async (att) => {
    try {
      await deleteAttachment({ token: auth?.accessToken, attachmentId: att._id, task: taskId }).unwrap();
    } catch (e) {
      showError(errText(e, "Ek silinemedi."));
    }
  };

  return (
    <li className="flex gap-3" data-testid="task-comment">
      <UserChip user={comment.author} showName={false} />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 text-xs text-gray-500">
          <span className="text-sm font-Medium text-gray-800">
            {`${comment.author?.name || ""} ${comment.author?.lastName || ""}`.trim()}
          </span>
          <span title={formatDateTime(comment.createdAt)}>{relativeTime(comment.createdAt)}</span>
          {comment.editedAt && <span>(düzenlendi)</span>}
          <span className="ml-auto flex gap-2">
            {comment.can?.edit && draft === null && (
              <button type="button" onClick={() => setDraft(comment.body || "")} className="hover:text-main">
                Düzenle
              </button>
            )}
            {comment.can?.delete && (
              <button type="button" onClick={() => onDelete(comment)} className="hover:text-fifth">
                Sil
              </button>
            )}
          </span>
        </div>
        {draft !== null ? (
          <div className="mt-2 space-y-2">
            <RichTextEditor
              value={draft}
              onChange={setDraft}
              onUploadImage={onUploadImage}
              resolveImage={resolveImage}
            />
            <div className="flex justify-end gap-2">
              <button type="button" className={btnGhost} onClick={() => setDraft(null)} disabled={saving}>
                Vazgeç
              </button>
              <button
                type="button"
                className={btnPrimary}
                onClick={save}
                disabled={saving || !draft || draft.length > MAX_BODY}
              >
                Kaydet
              </button>
            </div>
          </div>
        ) : (
          <RichTextView html={comment.body} resolveImage={resolveImage} className="mt-1" />
        )}
        {fileAttachments.length > 0 && (
          <div className="mt-2">
            <AttachmentList
              attachments={fileAttachments}
              onDelete={removeAttachment}
              modalZIndex={OVER_DRAWER_Z}
            />
          </div>
        )}
      </div>
    </li>
  );
}

// 2. ve sonraki sayfalar ayrı sorgu (her sayfa kendi cache girdisi; TaskComments tag'i hepsini tazeler)
function CommentPage({ taskId, page, renderItems }) {
  const { auth } = useAuth();
  const { data } = useGetTaskCommentsQuery({ token: auth?.accessToken, task: taskId, page, limit: COMMENT_PAGE });
  return renderItems(data?.data?.comments || []);
}

function Composer({ taskId, upload, resolveImage }) {
  const { auth } = useAuth();
  const { showError } = useAlert();
  const [createComment, { isLoading: sending }] = useCreateTaskCommentMutation();
  const [deleteAttachment] = useDeleteTaskAttachmentMutation();
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]); // yüklenmiş ekler (Attachment)
  const [progress, setProgress] = useState(null);
  const inlineIdsRef = useRef(new Set()); // bu yoruma yapıştırılan görsellerin ek id'leri
  const inputRef = useRef(null);
  useDirtyDraft(Boolean(body) || files.length > 0);

  const onUploadImage = async (file) => {
    const id = (await upload(file))?._id;
    if (id) inlineIdsRef.current.add(id);
    return id;
  };

  const attach = async (list) => {
    for (const file of Array.from(list || [])) {
      try {
        setProgress({ name: file.name, pct: 0 });
        // eslint-disable-next-line no-await-in-loop
        const att = await upload(file, { onProgress: (pct) => setProgress({ name: file.name, pct }) });
        if (att) setFiles((prev) => [...prev, att]);
      } catch (e) {
        showError(errText(e, "Dosya yüklenemedi."));
      }
    }
    setProgress(null);
  };

  const unattach = async (att) => {
    setFiles((prev) => prev.filter((f) => f._id !== att._id));
    try {
      await deleteAttachment({ token: auth?.accessToken, attachmentId: att._id, task: taskId }).unwrap();
    } catch (e) {
      // yorumsuz (pending/ready) ek sunucuda kalsa da 24 saatlik temizlik/ek listesi kapsar
    }
  };

  const submit = async () => {
    if (!body || sending) return;
    // Yapıştırılan görseller yalnız gövdede hâlâ duruyorsa yoruma bağlanır
    const inBody = new Set(inlineAttachmentIds(body));
    const inline = [...inlineIdsRef.current].filter((id) => inBody.has(id));
    const attachmentIds = [...new Set([...files.map((f) => f._id), ...inline])];
    try {
      await createComment({
        token: auth?.accessToken,
        task: taskId,
        body,
        attachmentIds,
      }).unwrap();
      setBody("");
      setFiles([]);
      inlineIdsRef.current = new Set();
    } catch (e) {
      showError(errText(e, "Yorum gönderilemedi."));
    }
  };

  const tooLong = body.length > MAX_BODY;

  return (
    <div className="space-y-2" data-testid="comment-composer">
      <RichTextEditor
        value={body}
        onChange={setBody}
        onUploadImage={onUploadImage}
        resolveImage={resolveImage}
        placeholder="Yorum yazın..."
      />
      {files.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {files.map((f) => (
            <li key={f._id} className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700">
              {f.name}
              <button type="button" onClick={() => unattach(f)} aria-label={`${f.name} kaldır`} className="text-gray-400 hover:text-fifth">
                <FaTimes className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {progress && (
        <p className="text-xs text-gray-500">
          {progress.name} yükleniyor... %{progress.pct}
        </p>
      )}
      {tooLong && <p className="text-xs text-fifth">Yorum en fazla 20 KB olabilir.</p>}
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={Boolean(progress)}
          className="inline-flex items-center gap-1 text-xs font-Medium text-gray-500 hover:text-main disabled:opacity-50"
        >
          <FaPaperclip className="w-3 h-3" aria-hidden="true" />
          Dosya ekle
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          data-testid="comment-file-input"
          onChange={(e) => {
            attach(e.target.files);
            e.target.value = "";
          }}
        />
        <button
          type="button"
          className={btnPrimary}
          onClick={submit}
          disabled={!body || sending || tooLong || Boolean(progress)}
        >
          {sending ? "Gönderiliyor..." : "Gönder"}
        </button>
      </div>
    </div>
  );
}

function TaskComments({ taskId, canComment }) {
  const { auth } = useAuth();
  const { showError, showSuccess } = useAlert();
  const [pages, setPages] = useState(1);
  const [toDelete, setToDelete] = useState(null);
  const { upload } = useAttachmentUpload(taskId);
  const resolveImage = useTaskImageResolver();
  const [deleteComment, { isLoading: deleting }] = useDeleteTaskCommentMutation();
  const { data, isLoading } = useGetTaskCommentsQuery({
    token: auth?.accessToken,
    task: taskId,
    page: 1,
    limit: COMMENT_PAGE,
  });
  const first = data?.data?.comments || [];
  const total = data?.data?.total || 0;

  const onUploadImage = async (file) => (await upload(file))?._id;
  const renderItems = (list) =>
    list.map((c) => (
      <CommentItem
        key={c._id}
        comment={c}
        taskId={taskId}
        resolveImage={resolveImage}
        onUploadImage={onUploadImage}
        onDelete={setToDelete}
      />
    ));

  const confirmDelete = async () => {
    try {
      await deleteComment({ token: auth?.accessToken, id: toDelete._id, task: taskId }).unwrap();
      showSuccess("Yorum silindi.");
      setToDelete(null);
    } catch (e) {
      showError(errText(e, "Yorum silinemedi."));
    }
  };

  return (
    <div className="space-y-4">
      {isLoading ? (
        <p className="text-sm text-gray-500">Yorumlar yükleniyor...</p>
      ) : total === 0 ? (
        <p className="text-sm text-gray-500">Henüz yorum yok.</p>
      ) : (
        <ul className="space-y-4">
          {renderItems(first)}
          {Array.from({ length: pages - 1 }, (_, i) => (
            <CommentPage key={i + 2} taskId={taskId} page={i + 2} renderItems={renderItems} />
          ))}
        </ul>
      )}
      {total > pages * COMMENT_PAGE && (
        <button type="button" onClick={() => setPages((p) => p + 1)} className={btnGhost}>
          Daha fazla ({total - pages * COMMENT_PAGE})
        </button>
      )}
      {canComment && <Composer taskId={taskId} upload={upload} resolveImage={resolveImage} />}
      <TaskConfirmModal
        open={Boolean(toDelete)}
        title="Yorumu sil"
        message="Bu yorum silinecek. Emin misiniz?"
        busy={deleting}
        onConfirm={confirmDelete}
        onClose={() => setToDelete(null)}
      />
    </div>
  );
}

export default TaskComments;
